// import { BREAKPOINT } from '@/constants';

class CloverModal extends HTMLElement {
  constructor(self) {
    self = super(self);
    this._returnFocus = null;
    this._focusEl = null;
    this.hidden = false;
    this._container = null;
    this._scrollbarWidth = null;
    this._menubar = document.querySelector('[data-menubar]');
    this._bgVideo = document.getElementById('masthead-video');
    return self;
  }

  static get is() {
    return 'clover-modal';
  }

  static get observedAttributes() {
    return ['open'];
  }

  static get CLASS_NAMES() {
    return {
      OPEN: 'is-open',
      ANIMATING: 'is-animating'
    };
  }

  attributeChangedCallback(name, val, newVal) {
    switch (name) {
      case 'open':
        this._openChanged();
    }
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(value) {
    if (Boolean(value)) {
      this.setAttribute('open', '');
    } else {
      this.removeAttribute('open');
    }
  }

  get fullScreen() {
    return this.hasAttribute('full-screen');
  }

  set fullScreen(value) {
    if (Boolean(value)) {
      this.setAttribute('full-screen', '');
    } else {
      this.removeAttribute('full-screen');
    }
  }

  get embed() {
    return this.hasAttribute('embed');
  }

  set embed(value) {
    if (Boolean(value)) {
      this.setAttribute('embed', '');
    } else {
      this.removeAttribute('embed');
    }
  }

  get timeout() {
    return this.getAttribute('timeout');
  }

  set timeout(value) {
    if (value) {
      this.setAttribute('timeout', Number(value));
    } else {
      this.removeAttribute('timeout');
    }
  }

  /**
   * Getter for `prefix` attribute
   */
  get prefix() {
    return this.hasAttribute('prefix')
      ? this.getAttribute('prefix')
      : 'c-modal';
  }

  /**
   * Setter for `prefix` attribute
   */
  set prefix(value) {
    if (String(value.length)) {
      this.setAttribute('prefix', value);
    } else {
      this.removeAttribute('prefix');
    }
  }

  get _buttons() {
    return (
      [...document.querySelectorAll(`a[href="#${this.id}"]`)] && [
        ...document.querySelectorAll(`[data-js-modal="#${this.id}"]`)
      ]
    );
  }

  connectedCallback() {
    if (this._created) {
      return;
    }

    this._created = true;
    document.body.appendChild(this);
    this.classList.add(this.prefix);

    // Bind handlers
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handleBtnClick = this._handleBtnClick.bind(this);
    this._handleModalOpen = this._handleModalOpen.bind(this);
    this._handleHashChange = this._handleHashChange.bind(this);

    // Setup HTML
    this.innerHTML = this._template;
    this._container = this.querySelector(`.${this.prefix}__inner`);
    this._content = this.querySelector(`.${this.prefix}__content`);

    // Add role of 'dialog'
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'dialog');
    }

    this._focusEl = this.querySelector('[data-focus]');

    // Add click listener to close modal on click (bounding container element)
    this.addEventListener('click', this._handleClick, false);
    this.addEventListener('modalclose', this._toggle, false);
    window.addEventListener('modalopen', this._handleModalOpen, false);
    window.addEventListener('hashchange', this._handleHashChange, false);

    // Add click listeners to open modals
    if (this._buttons.length) {
      this._buttons.forEach(btn => {
        btn.addEventListener('click', this._handleBtnClick, false);
      });
    }

    // If hash in location, open modal
    if (window.location.hash === `#${this.id}`) {
      this.open = true;
    }

    // Check if already open
    if (this.open) {
      this._open();
    }

    // Check timeout
    if (this.timeout) {
      this._setTimeout();
    }
  }

  disconnectedCallback() {
    if (this._buttons.length) {
      this._buttons.forEach(btn => {
        btn.removeEventListener(
          'click',
          this._handleBtnClick.bind(this),
          false
        );
      });
    }

    window.removeEventListener('keydown', this._handleKeydown, false);
    window.removeEventListener('modalopen', this._handleModalOpen, false);
    window.removeEventListener('hashchange', this._handleHashChange, false);
  }

  _openChanged() {
    if (this.open) {
      this._open();
    } else {
      this._close();
    }
  }

  _handleModalOpen(evt) {
    if (evt.detail === `${this.id}`) {
      this._handleBtnClick(evt);
    }
  }

  _handleClick(evt) {
    if (evt.target === this || evt.target === this._container) {
      this.open = false;
    }
  }

  /**
   *
   *
   * @param {UIEvent} evt
   * @memberof CloverModal
   */
  _handleBtnClick(evt) {
    // Only prevent default action if the button doesn't have a target, i.e., download button opens a file in a new tab and opens modal in current tab.
    if (!evt.target.hasAttribute('target')) {
      evt.preventDefault();
    }
    evt.stopPropagation();
    this._returnFocus = evt.target;
    this._toggle();
  }

  _toggle() {
    if (this.open) {
      this.open = false;
    } else {
      this.open = true;
    }
  }

  _setTimeout() {
    if (!this.timeout) {
      return;
    }

    setTimeout(() => {
      this.open = true;
      this.timeout = false;
    }, this.timeout);
  }

  _open() {
    this.scrollTop = 0;
    this._content.scrollTop = 0;

    // If embed, autoplay
    if (this.embed) {
      this._autoplay();
    }

    // Check scrollbar
    this._checkScrollbar();
    this._adjustDialog();

    document.body.classList.add('modal-is-open');

    // Add keyboard listener
    window.addEventListener('keydown', this._handleKeydown, false);

    // Push state
    // window.location.hash = this.id;
    window.history.pushState({}, null, `#${this.id}`);

    // Progressive enhancement
    this._animate().then(() => {
      this.classList.add(CloverModal.CLASS_NAMES.OPEN);

      this.style.right = null;

      if (this._focusEl) {
        if (!this.tabIndex) {
          this.tabIndex = '-1';
        }
        this._focusEl.focus();
      } else {
        this.tabIndex = '-1';
        this.focus();
      }
    });

    // [...document.body.children].forEach(child => {
    //   if (child !== this) {
    //     child.inert = true;
    //   }
    // });

    // Update layout on sliders
    customElements.whenDefined('tiny-slider').then(() => {
      [...this.querySelectorAll('tiny-slider')].forEach(el => {
        el.reset();
      });
    });

    // Pause video
    if (this._bgVideo) {
      this._bgVideo.pause();
    }

    // Tracking
    if (this.fullScreen) {
      let data = window.__pageData || {};

      this.$emit('pageview', {
        clickThroughSource: {
          page: data['page_name'],
          element: this._returnFocus ? 'modal-btn' : '',
          source_type: data['platform']
        },
        hash: this.id
      });
    }
  }

  _animate() {
    return new Promise((resolve, reject) => {
      // Wait until Element has rendered
      window.requestAnimationFrame(() => {
        const TIMEOUT = 500;

        // Add animating class
        this.classList.add(CloverModal.CLASS_NAMES.ANIMATING);

        window.requestAnimationFrame(() => {
          // Toggle open class
          if (this.open) {
            this.classList.add(CloverModal.CLASS_NAMES.OPEN);
          } else {
            this.classList.remove(CloverModal.CLASS_NAMES.OPEN);
          }

          // Add event transitionend event listener, and resolve Promise once triggered
          this.$once('transitionend', () => {
            this.classList.remove(CloverModal.CLASS_NAMES.ANIMATING);
            resolve();
          });

          // Set long timeout to reject Promise if `transitionend` listener is never called
          setTimeout(() => {
            this.classList.remove(CloverModal.CLASS_NAMES.ANIMATING);
            resolve();
          }, TIMEOUT);
        });
      });
    });
  }

  get _isAnimating() {
    return this.classList.contains(CloverModal.CLASS_NAMES.ANIMATING);
  }

  _close() {
    // Adjust right
    if (this.fullScreen && this._isModalOverflowing) {
      this.style.right = `${this._scrollbarWidth}px`;
    }

    this._animate().then(() => {
      this.classList.remove(CloverModal.CLASS_NAMES.OPEN);
      this.style.paddingLeft = '';
      this.style.paddingRight = '';
      this.style.right = null;
      this._menubar.style.paddingRight = '';
      document.documentElement.style.marginRight = '';
      document.body.classList.remove('modal-is-open');

      // Return focus to where user opened it from
      if (this._returnFocus) {
        this._returnFocus.focus();
        this._returnFocus = null;
      }

      window.history.replaceState(
        {},
        null,
        window.location.origin + window.location.pathname
      );

      // Tracking
      if ('utag' in window) {
        if (this.fullScreen) {
          let data = window.__pageData || {};

          this.$emit('pageview', {
            clickThroughSource: {
              page: data['page_name'] + `:${this.id}`,
              element: 'modal-close-btn',
              source_type: data['platform']
            }
          });
        }
      }
    });

    if (this.embed) {
      this._removeAutoplay();
    }

    // Play video
    if (this._bgVideo) {
      this._bgVideo.play();
    }

    // Remove keyboard listener
    window.removeEventListener('keydown', this._handleKeydown, false);

    // [...document.body.children].forEach(child => {
    //   if (child !== this) {
    //     child.inert = false;
    //   }
    // });
  }

  _handleHashChange() {
    if (window.location.hash === `#${this.id}`) {
      this.open = true;
    } else {
      this.open = false;
    }
  }

  _handleKeydown(evt) {
    if (evt.keyCode === 27) {
      this.open = false;
    }
  }

  get _template() {
    return `
      <div class="${this.prefix}__inner">
        <div class="${this.prefix}__content">
          ${this.innerHTML}
        </div>
      <button 
       type="button"
       class="${this.prefix}__close" 
       onclick="$emit('modalclose')"
      >
        <span class="sr-only">X Close</span>
      </button>
      </div>
    `;
  }

  _checkScrollbar() {
    const rect = document.body.getBoundingClientRect();

    this._isBodyOverflowing = rect.left + rect.right < window.innerWidth;
    this._scrollbarWidth = this._getScrollbarWidth();
  }

  _getScrollbarWidth() {
    const scrollDiv = document.createElement('div');

    scrollDiv.className = 'scrollbar-measure';
    document.body.appendChild(scrollDiv);
    const scrollbarWidth =
      scrollDiv.getBoundingClientRect().width - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);

    return scrollbarWidth;
  }

  _adjustDialog() {
    if (this.fullScreen) {
      if (this._isModalOverflowing) {
        this.style.right = `${this._scrollbarWidth}px`;
      }
    } else {
      if (!this._isBodyOverflowing && this._isModalOverflowing) {
        this.style.paddingLeft = `${this._scrollbarWidth}px`;
      }

      if (this._isBodyOverflowing && !this._isModalOverflowing) {
        this.style.paddingRight = `${this._scrollbarWidth}px`;
      }
    }

    this._menubar.style.paddingRight = `${this._scrollbarWidth}px`;
    document.documentElement.style.marginRight = `${this._scrollbarWidth}px`;
  }

  _autoplay() {
    const video = this.querySelector('iframe, video');

    // If no video or iframe, return
    if (!video) {
      return;
    }

    // Replace data-src with src
    video.src = video.getAttribute('data-src');

    // Tracking
    this.$emit('pageevent', {
      schema: 'iglu:com.reagroup/video_played/jsonschema/1-0-0',
      data: {
        event_name: 'video_played',
        video_name: video.hasAttribute('data-utag-video-name')
          ? video.getAttribute('data-utag-video-name')
          : '',
        video_platform: video.hasAttribute('data-utag-video-platform')
          ? video.getAttribute('data-utag-video-platform')
          : '',
        video_autoplay: 'no'
      }
    });
  }

  _removeAutoplay() {
    const video = this.querySelector('iframe, video');

    // If no video or iframe, return
    if (!video) {
      return;
    }

    video.src = '';
  }

  get _isModalOverflowing() {
    return this.scrollHeight > document.documentElement.clientHeight;
  }
}

customElements.define(CloverModal.is, CloverModal);
